// packages import
import { Autocomplete, TextField } from '@mui/material'
import { FC, useEffect, useState } from 'react'
import { useDebounce } from 'use-debounce'

// others
import { Office, OfficeType, useFetchAllOfficesLazyQuery } from '../../../generated'
import { OfficeDropdownProps } from '../../interfaceTypes/props'
import { GRAPHQL_QUERY_POLICY_CACHE_FIRST } from '../../constants'

const OfficeDropdown: FC<OfficeDropdownProps> = ({
  selectedOffice,
  onChange,
  label = 'Profit Center',
  placeholder,
  errorMessage,
  disabled = false,
  size = 'medium',
  isProfitCenter = true,
  autoSelectOnTab = true,
  sx = { minWidth: 150 }
}) => {
  const [offices, setOffices] = useState<Office[]>([])
  const [searchTerm, setSearchTerm] = useState<string>('')

  const value = offices?.find(office => office?.name === selectedOffice) || null

  const [debouncedSearchTerm] = useDebounce(searchTerm, 500)

  const [fetchOffices, { loading: officeLoading }] = useFetchAllOfficesLazyQuery({
    ...GRAPHQL_QUERY_POLICY_CACHE_FIRST,
    notifyOnNetworkStatusChange: true,
    variables: undefined,
    onError() {},

    onCompleted(data) {
      const { fetchAllOffices } = data || {}
      const { offices: fetchedOffices } = fetchAllOffices || {}
      if (fetchedOffices && fetchedOffices.length > 0) {
        setOffices(prevOffices => {
          const validOffices = fetchedOffices.filter(
            (office): office is Office =>
              office !== null &&
              office !== undefined &&
              (isProfitCenter ? office?.type === OfficeType.ProfitCenter : office?.type === OfficeType.Location)
          )
          return validOffices
        })
      }
    }
  })

  const handleOfficeChange = (newValue: Office) => {
    onChange(newValue)
  }

  useEffect(() => {
    fetchOffices({
      variables: {
        fetchOfficesInput: {
          keyword: debouncedSearchTerm
        }
      }
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearchTerm])

  return (
    <Autocomplete
      sx={sx}
      options={offices ?? []}
      value={value}
      onChange={(_, newValue) => handleOfficeChange(newValue as Office)}
      onInputChange={(_, newInputValue) => setSearchTerm(newInputValue)}
      getOptionLabel={option =>
        option?.officeNumber ? `(#${option.officeNumber}) ${option?.name || ''}`.trim() : `${option?.name || ''}`.trim()
      }
      loading={officeLoading}
      isOptionEqualToValue={(option, value) => option?.id === value?.id}
      disabled={disabled}
      autoHighlight={autoSelectOnTab}
      autoSelect={autoSelectOnTab}
      ListboxProps={{
        role: 'list-box',
        style: {
          maxHeight: '190px'
        }
      }}
      renderInput={params => (
        <TextField
          {...params}
          label={label}
          placeholder={placeholder}
          error={!!errorMessage}
          helperText={errorMessage}
          size={size}
        />
      )}
    />
  )
}

export default OfficeDropdown
